import React, { useState, useEffect } from 'react';
import { Building2, Mail, FileText, Euro, Plus, X, Save, Trash2, AlertCircle, CheckCircle2, Crown, Shield, Edit2, DollarSign, Lock, FileText as FileText2, Flame } from 'lucide-react';
import {
  fetchCompanySubscription,
  fetchSubscriptionAddons,
  fetchServicePricing,
  fetchCompanyBillingSettings,
  updateServicePricing,
  createServicePricing,
  deleteServicePricing,
  updateCompanyBillingSettings,
  fetchVatEntities,
  createVatEntity,
  updateVatEntity,
  deleteVatEntity,
  isMagicAdmin,
  getCurrentUserCompany,
  type Company,
  type CompanySubscription,
  type SubscriptionAddon,
  type ServicePricing,
  type CompanyBillingSettings,
  type VatEntity
} from '../lib/supabase';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  type: 'invoice' | 'reminder' | 'confirmation';
  active: boolean;
}

const mockEmailTemplates: EmailTemplate[] = [
  {
    id: 'ET1',
    name: 'Default Invoice',
    subject: 'Your invoice from Jaimy - {{invoice_number}}',
    type: 'invoice',
    active: true
  },
  {
    id: 'ET2',
    name: 'Payment Reminder',
    subject: 'Reminder: Outstanding invoice {{invoice_number}}',
    type: 'reminder',
    active: true
  }
];

export function Settings() {
  const [activeTab, setActiveTab] = useState('subscription');
  const [company, setCompany] = useState<Company | null>(null);
  const [subscription, setSubscription] = useState<CompanySubscription | null>(null);
  const [addons, setAddons] = useState<SubscriptionAddon[]>([]);
  const [servicePricing, setServicePricing] = useState<ServicePricing[]>([]);
  const [billingSettings, setBillingSettings] = useState<CompanyBillingSettings | null>(null);
  const [vatEntities, setVatEntities] = useState<VatEntity[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>(mockEmailTemplates);
  const [isLoading, setIsLoading] = useState(true);
  const [isMagicAdminUser, setIsMagicAdminUser] = useState(false);
  const [isAddingVatEntity, setIsAddingVatEntity] = useState(false);
  const [isEditingVatEntity, setIsEditingVatEntity] = useState(false);
  const [selectedVatEntity, setSelectedVatEntity] = useState<VatEntity | null>(null);
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [editingPricing, setEditingPricing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAddingService, setIsAddingService] = useState(false);

  const [newVatEntity, setNewVatEntity] = useState({
    name: '',
    vat_number: '',
    address: '',
    is_default: false
  });

  const [newService, setNewService] = useState({
    service_type: 'other',
    service_name: '',
    base_price: 0,
    handling_fee: 0,
    configuration: {
      subtitle: '',
      handling_time: 60,
      additional_hour_price: 45
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if user is magic admin
      const magicAdmin = await isMagicAdmin();
      setIsMagicAdminUser(magicAdmin);

      // Get current company
      const currentCompany = await getCurrentUserCompany();
      if (!currentCompany) {
        throw new Error('No company found for current user');
      }
      setCompany(currentCompany);

      // Load company-specific data
      const [subscriptionData, addonsData, pricingData, billingData, vatEntitiesData] = await Promise.all([
        fetchCompanySubscription(currentCompany.id),
        fetchSubscriptionAddons(),
        fetchServicePricing(currentCompany.id),
        fetchCompanyBillingSettings(currentCompany.id),
        fetchVatEntities(currentCompany.id)
      ]);

      setSubscription(subscriptionData);
      setAddons(addonsData);
      setServicePricing(pricingData);
      setBillingSettings(billingData);
      setVatEntities(vatEntitiesData);

      console.log('Settings data loaded for company:', currentCompany.name, {
        subscription: !!subscriptionData,
        pricingItems: pricingData.length,
        billingSettings: !!billingData,
        vatEntities: vatEntitiesData.length,
        isMagicAdmin: magicAdmin
      });
    } catch (error) {
      console.error('Error loading settings data:', error);
      setError('Failed to load settings data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveVatEntity = async () => {
    if (!company) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const vatEntityData = {
        ...newVatEntity,
        company_id: company.id
      };
      
      await createVatEntity(vatEntityData);
      
      // Refresh VAT entities
      const updatedVatEntities = await fetchVatEntities(company.id);
      setVatEntities(updatedVatEntities);
      
      setIsAddingVatEntity(false);
      setNewVatEntity({
        name: '',
        vat_number: '',
        address: '',
        is_default: false
      });
      
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error creating VAT entity:', error);
      setError('Failed to create VAT entity');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateVatEntity = async () => {
    if (!company || !selectedVatEntity) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      await updateVatEntity(selectedVatEntity.id, {
        ...newVatEntity,
        company_id: company.id
      });
      
      // Refresh VAT entities
      const updatedVatEntities = await fetchVatEntities(company.id);
      setVatEntities(updatedVatEntities);
      
      setIsEditingVatEntity(false);
      setSelectedVatEntity(null);
      setNewVatEntity({
        name: '',
        vat_number: '',
        address: '',
        is_default: false
      });
      
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating VAT entity:', error);
      setError('Failed to update VAT entity');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetDefaultVatEntity = async (id: string) => {
    if (!company) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      await updateVatEntity(id, {
        is_default: true,
        company_id: company.id
      });
      
      // Refresh VAT entities
      const updatedVatEntities = await fetchVatEntities(company.id);
      setVatEntities(updatedVatEntities);
      
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error setting default VAT entity:', error);
      setError('Failed to set default VAT entity');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteVatEntity = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      await deleteVatEntity(id);
      
      // Refresh VAT entities
      if (company) {
        const updatedVatEntities = await fetchVatEntities(company.id);
        setVatEntities(updatedVatEntities);
      }
      
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error deleting VAT entity:', error);
      setError('Failed to delete VAT entity');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateServicePricing = async (pricingId: string, updates: Partial<ServicePricing>) => {
    if (!isMagicAdminUser) {
      setError('Only Magic Admins can modify service pricing');
      return;
    }

    try {
      await updateServicePricing(pricingId, updates);
      setServicePricing(prev => prev.map(pricing => 
        pricing.id === pricingId ? { ...pricing, ...updates } : pricing
      ));
      setEditingPricing(null);
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating service pricing:', error);
      setError('Failed to update service pricing');
    }
  };

  const handleCreateService = async () => {
    if (!company) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Check if service with this type already exists
      const existingService = servicePricing.find(
        service => service.service_type === newService.service_type
      );
      
      if (existingService) {
        // Update existing service instead of creating a new one
        console.log('Service type already exists, updating instead of creating new:', newService.service_type);
        await updateServicePricing(existingService.id, {
          service_name: newService.service_name,
          base_price: newService.base_price,
          handling_fee: newService.handling_fee,
          configuration: newService.configuration
        });
        
        console.log('Service updated successfully');
      } else {
        // Create new service
        await createServicePricing({
          company_id: company.id,
          service_type: newService.service_type,
          service_name: newService.service_name,
          base_price: newService.base_price,
          handling_fee: newService.handling_fee,
          is_active: true,
          configuration: newService.configuration
        });
        
        console.log('Service created successfully');
      }
      
      // Refresh service pricing
      const updatedPricing = await fetchServicePricing(company.id);
      setServicePricing(updatedPricing);
      
      setIsAddingService(false);
      setNewService({
        service_type: 'other',
        service_name: '',
        base_price: 0,
        handling_fee: 0,
        configuration: {
          subtitle: '',
          handling_time: 60,
          additional_hour_price: 45
        }
      });
      
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error creating service pricing:', error);
      setError(`Failed to create service pricing: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteService = async (id: string) => {
    if (!isMagicAdminUser) {
      setError('Only Magic Admins can delete service pricing');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      await deleteServicePricing(id);
      
      // Refresh service pricing
      if (company) {
        const updatedPricing = await fetchServicePricing(company.id);
        setServicePricing(updatedPricing);
      }
      
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error deleting service pricing:', error);
      setError('Failed to delete service pricing');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateBillingSettings = async (updates: Partial<CompanyBillingSettings>) => {
    if (!company) return;
    
    // Check if trying to update magic admin only fields
    const magicAdminOnlyFields = ['default_handling_fee'];
    const hasMagicAdminOnlyUpdates = magicAdminOnlyFields.some(field => field in updates);
    
    if (hasMagicAdminOnlyUpdates && !isMagicAdminUser) {
      setError('Only Magic Admins can modify handling fees');
      return;
    }
    
    try {
      const updatedSettings = await updateCompanyBillingSettings(company.id, updates);
      setBillingSettings(updatedSettings);
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating billing settings:', error);
      setError('Failed to update billing settings');
    }
  };

  const calculateTotalMonthlyPrice = () => {
    if (!subscription || !addons) return 0;
    
    const basePrice = subscription.base_monthly_price;
    const addonPrice = subscription.addon_ids.reduce((total, addonId) => {
      const addon = addons.find(a => a.id === addonId);
      return total + (addon?.monthly_price || 0);
    }, 0);
    
    return basePrice + addonPrice;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-6 w-6 text-red-600" />
          <div>
            <h3 className="text-lg font-semibold text-red-800">Error Loading Settings</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
        <button
          onClick={loadData}
          className="mt-4 gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            {isMagicAdminUser && (
              <Crown className="h-6 w-6 text-yellow-500" title="Magic Admin" />
            )}
          </div>
          <p className="text-gray-600 mt-1">
            Manage settings for <span className="font-medium">{company?.name}</span>
          </p>
          {!isMagicAdminUser && (
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
              <Lock className="h-4 w-4" />
              Some settings require Magic Admin privileges to modify
            </p>
          )}
        </div>
      </div>

      {/* Settings Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['subscription', 'pricing', 'billing', 'invoicing', 'vat', 'email'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm capitalize
                ${activeTab === tab
                  ? 'border-jaimy-red text-jaimy-red'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="fixed bottom-4 right-4 bg-green-100 text-green-800 px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg z-50">
          <CheckCircle2 className="h-5 w-5" />
          Settings saved successfully
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Settings Content */}
      <div className="space-y-6">
        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="space-y-6">
            {/* Current Subscription */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Current Subscription</h2>
              
              {subscription && company ? (
                <div className="space-y-4">
                  {/* Base License */}
                  <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-blue-900">
                          BizComfort {company.subscription_tier.charAt(0).toUpperCase() + company.subscription_tier.slice(1)} License
                        </h3>
                        <p className="text-blue-800 text-sm">Base platform access and core features</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-blue-900">€{subscription.base_monthly_price}</span>
                        <p className="text-xs text-blue-700">per month</p>
                      </div>
                    </div>
                  </div>

                  {/* Active Add-ons */}
                  {subscription.addon_ids.length > 0 && (
                    <div>
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Active Add-ons</h4>
                      <div className="space-y-2">
                        {subscription.addon_ids.map(addonId => {
                          const addon = addons.find(a => a.id === addonId);
                          if (!addon) return null;
                          
                          return (
                            <div key={addonId} className="border border-green-200 rounded-lg p-3 bg-green-50">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h5 className="font-medium text-green-900">{addon.name}</h5>
                                  <p className="text-sm text-green-800">{addon.description}</p>
                                </div>
                                <span className="text-lg font-semibold text-green-900">€{addon.monthly_price}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Total */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between text-xl font-bold">
                      <span>Total Monthly Cost</span>
                      <span className="text-jaimy-red">€{calculateTotalMonthlyPrice()}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No subscription data available for this company</p>
                </div>
              )}
            </div>

            {/* Billing Information */}
            {subscription && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Billing Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Billing Contact</h4>
                    <p className="text-gray-900">{subscription.billing_contact_name}</p>
                    <p className="text-gray-600">{subscription.billing_contact_email}</p>
                    <p className="text-gray-600">{subscription.billing_contact_phone}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Billing Address</h4>
                    <p className="text-gray-900">{subscription.billing_address}</p>
                    {subscription.vat_number && (
                      <p className="text-gray-600">VAT: {subscription.vat_number}</p>
                    )}
                    <p className="text-gray-600">Payment Terms: {subscription.payment_terms_days} days</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Service Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Service Pricing</h2>
                <p className="text-gray-600 text-sm">
                  Company-specific pricing for {company?.name}
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  {isMagicAdminUser ? 'You can modify all pricing settings' : 'Pricing can only be modified by Magic Admins'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {isMagicAdminUser && (
                  <button
                    onClick={() => setIsAddingService(true)}
                    className="gradient-bg text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity"
                  >
                    <Plus className="h-5 w-5" />
                    <span>Add Service</span>
                  </button>
                )}
                {isMagicAdminUser && (
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-yellow-500" />
                    <span className="text-sm text-yellow-600 font-medium">Magic Admin Access</span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {servicePricing.length === 0 ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No service pricing configured for this company</p>
                  {isMagicAdminUser && (
                    <button
                      onClick={() => setIsAddingService(true)}
                      className="mt-4 gradient-bg text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity mx-auto"
                    >
                      <Plus className="h-5 w-5" />
                      <span>Add Service</span>
                    </button>
                  )}
                </div>
              ) : (
                servicePricing.map((pricing) => (
                  <div key={pricing.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{pricing.service_name}</h3>
                        <p className="text-sm text-gray-600 capitalize">{pricing.service_type.replace('_', ' ')}</p>
                        {pricing.configuration?.subtitle && (
                          <p className="text-xs text-gray-500 mt-1">{pricing.configuration.subtitle}</p>
                        )}
                      </div>
                      
                      {editingPricing === pricing.id && isMagicAdminUser ? (
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Base Price:</label>
                            <div className="flex items-center">
                              <Euro className="h-4 w-4 text-gray-400" />
                              <input
                                type="number"
                                step="0.01"
                                defaultValue={pricing.base_price}
                                className="w-20 px-2 py-1 border border-gray-200 rounded text-sm"
                                onBlur={(e) => {
                                  const newPrice = parseFloat(e.target.value);
                                  if (!isNaN(newPrice)) {
                                    handleUpdateServicePricing(pricing.id, { base_price: newPrice });
                                  }
                                }}
                              />
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600">Handling Fee:</label>
                            <div className="flex items-center">
                              <Euro className="h-4 w-4 text-gray-400" />
                              <input
                                type="number"
                                step="0.01"
                                defaultValue={pricing.handling_fee}
                                className="w-20 px-2 py-1 border border-gray-200 rounded text-sm"
                                onBlur={(e) => {
                                  const newFee = parseFloat(e.target.value);
                                  if (!isNaN(newFee)) {
                                    handleUpdateServicePricing(pricing.id, { handling_fee: newFee });
                                  }
                                }}
                              />
                            </div>
                          </div>
                          
                          <button
                            onClick={() => setEditingPricing(null)}
                            className="text-green-600 hover:text-green-800"
                          >
                            <CheckCircle2 className="h-5 w-5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-gray-600">Base:</span>
                              <span className="font-semibold">€{pricing.base_price}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-sm text-gray-600">Fee:</span>
                              <span className="font-semibold">€{pricing.handling_fee}</span>
                              {!isMagicAdminUser && <Lock className="h-3 w-3 text-gray-400 ml-1" />}
                            </div>
                            <div className="flex items-center gap-1 text-jaimy-red">
                              <span className="text-sm">Total:</span>
                              <span className="font-bold">€{(pricing.base_price + pricing.handling_fee).toFixed(2)}</span>
                            </div>
                          </div>
                          
                          {isMagicAdminUser && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setEditingPricing(pricing.id)}
                                className="text-gray-400 hover:text-jaimy-red transition-colors"
                              >
                                <Edit2 className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteService(pricing.id)}
                                className="text-gray-400 hover:text-red-600 transition-colors"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Billing Settings Tab */}
        {activeTab === 'billing' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Billing Settings</h2>
                <p className="text-gray-600 text-sm">
                  Company-specific billing configuration for {company?.name}
                </p>
              </div>
            </div>
            
            {billingSettings ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Spending Limit
                    </label>
                    <div className="flex items-center">
                      <Euro className="h-5 w-5 text-gray-400 mr-2" />
                      <input
                        type="number"
                        step="0.01"
                        defaultValue={billingSettings.monthly_spending_limit}
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                        onBlur={(e) => {
                          const newLimit = parseFloat(e.target.value);
                          if (!isNaN(newLimit)) {
                            handleUpdateBillingSettings({ monthly_spending_limit: newLimit });
                          }
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Maximum amount that can be invoiced monthly for this company
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Default Handling Fee
                      {!isMagicAdminUser && <Lock className="h-4 w-4 inline ml-1 text-gray-400" />}
                    </label>
                    <div className="flex items-center">
                      <Euro className="h-5 w-5 text-gray-400 mr-2" />
                      <input
                        type="number"
                        step="0.01"
                        defaultValue={billingSettings.default_handling_fee}
                        disabled={!isMagicAdminUser}
                        className={`flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent ${
                          !isMagicAdminUser ? 'bg-gray-50 cursor-not-allowed' : ''
                        }`}
                        onBlur={(e) => {
                          if (isMagicAdminUser) {
                            const newFee = parseFloat(e.target.value);
                            if (!isNaN(newFee)) {
                              handleUpdateBillingSettings({ default_handling_fee: newFee });
                            }
                          }
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {isMagicAdminUser 
                        ? 'Default handling fee for new services (Magic Admin only)'
                        : 'Only Magic Admins can modify handling fees'
                      }
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Day of Month
                    </label>
                    <select
                      defaultValue={billingSettings.billing_day_of_month}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      onChange={(e) => {
                        const newDay = parseInt(e.target.value);
                        handleUpdateBillingSettings({ billing_day_of_month: newDay });
                      }}
                    >
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        defaultChecked={billingSettings.auto_invoice_generation}
                        className="text-jaimy-red focus:ring-jaimy-red"
                        onChange={(e) => {
                          handleUpdateBillingSettings({ auto_invoice_generation: e.target.checked });
                        }}
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Auto-generate monthly invoices
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      Automatically create invoices on the billing day
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No billing settings configured for this company</p>
                {isMagicAdminUser && (
                  <button
                    onClick={() => handleUpdateBillingSettings({})}
                    className="mt-4 gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
                  >
                    Initialize Billing Settings
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* VAT Entities Tab */}
        {activeTab === 'vat' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">VAT Entities</h2>
                <p className="text-gray-600 text-sm">
                  Manage VAT entities for {company?.name}
                </p>
              </div>
              <button
                onClick={() => {
                  setNewVatEntity({
                    name: '',
                    vat_number: '',
                    address: '',
                    is_default: vatEntities.length === 0 // Make default if first entity
                  });
                  setIsAddingVatEntity(true);
                }}
                className="gradient-bg text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity"
              >
                <Plus className="h-5 w-5" />
                Add Entity
              </button>
            </div>

            <div className="space-y-4">
              {vatEntities.length === 0 ? (
                <div className="text-center py-8 border border-gray-200 rounded-lg">
                  <FileText2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">No VAT entities configured</p>
                  <p className="text-gray-500 text-sm mb-4">
                    VAT entities are used for invoicing and can be assigned to locations
                  </p>
                </div>
              ) : (
                vatEntities.map((entity) => (
                  <div
                    key={entity.id}
                    className={`border rounded-lg p-4 hover:border-jaimy-red transition-colors ${
                      entity.is_default ? 'border-jaimy-red bg-jaimy-red/5' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-medium text-gray-900">{entity.name}</h3>
                          {entity.is_default && (
                            <span className="bg-jaimy-red text-white text-xs px-2 py-0.5 rounded-full">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="mt-1 space-y-1 text-sm text-gray-600">
                          <p>VAT: {entity.vat_number}</p>
                          <p>{entity.address}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {!entity.is_default && (
                          <button
                            onClick={() => handleSetDefaultVatEntity(entity.id)}
                            className="text-sm text-gray-500 hover:text-jaimy-red transition-colors"
                          >
                            Set as Default
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedVatEntity(entity);
                            setNewVatEntity({
                              name: entity.name,
                              vat_number: entity.vat_number,
                              address: entity.address,
                              is_default: entity.is_default
                            });
                            setIsEditingVatEntity(true);
                          }}
                          className="text-gray-400 hover:text-jaimy-red transition-colors"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteVatEntity(entity.id)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          disabled={entity.is_default}
                          title={entity.is_default ? "Cannot delete default VAT entity" : "Delete VAT entity"}
                        >
                          <Trash2 className={`h-5 w-5 ${entity.is_default ? 'opacity-50 cursor-not-allowed' : ''}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Invoicing Tab */}
        {activeTab === 'invoicing' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Invoice Settings</h2>
              <p className="text-gray-600 text-sm">
                Configure invoice settings for {company?.name}
              </p>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Prefix
                  </label>
                  <input
                    type="text"
                    defaultValue={`${company?.name.substring(0, 3).toUpperCase()}-`}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This prefix will be added to all invoice numbers
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Payment Terms
                  </label>
                  <select
                    defaultValue="30"
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  >
                    <option value="15">15 days</option>
                    <option value="30">30 days</option>
                    <option value="45">45 days</option>
                    <option value="60">60 days</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Notes (Default)
                </label>
                <textarea
                  rows={3}
                  defaultValue="Thank you for your business. Please make payment to the account details listed on the invoice."
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="auto_send_invoices"
                  defaultChecked={true}
                  className="text-jaimy-red focus:ring-jaimy-red"
                />
                <label htmlFor="auto_send_invoices" className="text-sm font-medium text-gray-700">
                  Automatically send invoices when generated
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Email Templates Tab */}
        {activeTab === 'email' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Email Templates</h2>
              <p className="text-gray-600 text-sm">
                Company-specific email templates for {company?.name}
              </p>
            </div>
            
            <div className="space-y-4">
              {emailTemplates.map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-jaimy-red transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                      <p className="mt-1 text-sm text-gray-600">{template.subject}</p>
                      <span className={`mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {template.active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setSelectedTemplate(template);
                          setIsEditingTemplate(true);
                        }}
                        className="text-gray-400 hover:text-jaimy-red transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add VAT Entity Modal */}
      {isAddingVatEntity && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Add VAT Entity</h2>
              <button
                onClick={() => setIsAddingVatEntity(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Entity Name *
                </label>
                <input
                  type="text"
                  value={newVatEntity.name}
                  onChange={(e) => setNewVatEntity(prev => ({
                    ...prev,
                    name: e.target.value
                  }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  VAT Number *
                </label>
                <input
                  type="text"
                  value={newVatEntity.vat_number}
                  onChange={(e) => setNewVatEntity(prev => ({
                    ...prev,
                    vat_number: e.target.value
                  }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Address *
                </label>
                <textarea
                  value={newVatEntity.address}
                  onChange={(e) => setNewVatEntity(prev => ({
                    ...prev,
                    address: e.target.value
                  }))}
                  rows={3}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  required
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newVatEntity.is_default}
                  onChange={(e) => setNewVatEntity(prev => ({
                    ...prev,
                    is_default: e.target.checked
                  }))}
                  className="h-4 w-4 text-jaimy-red focus:ring-jaimy-red border-gray-300 rounded"
                  id="is_default"
                />
                <label htmlFor="is_default" className="ml-2 text-sm text-gray-600">
                  Set as default VAT entity
                </label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setIsAddingVatEntity(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveVatEntity}
                disabled={!newVatEntity.name || !newVatEntity.vat_number || !newVatEntity.address || isLoading}
                className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Entity
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit VAT Entity Modal */}
      {isEditingVatEntity && selectedVatEntity && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Edit VAT Entity</h2>
              <button
                onClick={() => {
                  setIsEditingVatEntity(false);
                  setSelectedVatEntity(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Entity Name *
                </label>
                <input
                  type="text"
                  value={newVatEntity.name}
                  onChange={(e) => setNewVatEntity(prev => ({
                    ...prev,
                    name: e.target.value
                  }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  VAT Number *
                </label>
                <input
                  type="text"
                  value={newVatEntity.vat_number}
                  onChange={(e) => setNewVatEntity(prev => ({
                    ...prev,
                    vat_number: e.target.value
                  }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Address *
                </label>
                <textarea
                  value={newVatEntity.address}
                  onChange={(e) => setNewVatEntity(prev => ({
                    ...prev,
                    address: e.target.value
                  }))}
                  rows={3}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  required
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newVatEntity.is_default}
                  onChange={(e) => setNewVatEntity(prev => ({
                    ...prev,
                    is_default: e.target.checked
                  }))}
                  className="h-4 w-4 text-jaimy-red focus:ring-jaimy-red border-gray-300 rounded"
                  id="edit_is_default"
                  disabled={selectedVatEntity.is_default} // Can't uncheck if already default
                />
                <label htmlFor="edit_is_default" className="ml-2 text-sm text-gray-600">
                  Set as default VAT entity
                </label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsEditingVatEntity(false);
                  setSelectedVatEntity(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateVatEntity}
                disabled={!newVatEntity.name || !newVatEntity.vat_number || !newVatEntity.address || isLoading}
                className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Update Entity
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Service Modal */}
      {isAddingService && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Add Service</h2>
              <button
                onClick={() => setIsAddingService(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Service Type *
                </label>
                <select
                  value={newService.service_type}
                  onChange={(e) => setNewService(prev => ({
                    ...prev,
                    service_type: e.target.value
                  }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  required
                >
                  <option value="electrical">Electrical Work</option>
                  <option value="heating">Heating Maintenance</option>
                  <option value="plumbing">Plumbing & Declogging</option>
                  <option value="inspection">Electrical Inspection</option>
                  <option value="safety">Safety Check</option>
                  <option value="installation">Installation Service</option>
                  <option value="other">Other Services</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Service Name *
                </label>
                <input
                  type="text"
                  value={newService.service_name}
                  onChange={(e) => setNewService(prev => ({
                    ...prev,
                    service_name: e.target.value
                  }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  required
                  placeholder="Enter service name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={newService.configuration.subtitle}
                  onChange={(e) => setNewService(prev => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      subtitle: e.target.value
                    }
                  }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  placeholder="Brief description of the service"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Base Price *
                  </label>
                  <div className="flex items-center mt-1">
                    <Euro className="h-5 w-5 text-gray-400 absolute ml-3" />
                    <input
                      type="number"
                      step="0.01"
                      value={newService.base_price}
                      onChange={(e) => setNewService(prev => ({
                        ...prev,
                        base_price: parseFloat(e.target.value) || 0
                      }))}
                      className="block w-full rounded-lg border border-gray-200 pl-10 pr-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Handling Fee *
                  </label>
                  <div className="flex items-center mt-1">
                    <Euro className="h-5 w-5 text-gray-400 absolute ml-3" />
                    <input
                      type="number"
                      step="0.01"
                      value={newService.handling_fee}
                      onChange={(e) => setNewService(prev => ({
                        ...prev,
                        handling_fee: parseFloat(e.target.value) || 0
                      }))}
                      className="block w-full rounded-lg border border-gray-200 pl-10 pr-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Handling Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={newService.configuration.handling_time}
                    onChange={(e) => setNewService(prev => ({
                      ...prev,
                      configuration: {
                        ...prev.configuration,
                        handling_time: parseInt(e.target.value) || 60
                      }
                    }))}
                    className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Additional Hour Price
                  </label>
                  <div className="flex items-center mt-1">
                    <Euro className="h-5 w-5 text-gray-400 absolute ml-3" />
                    <input
                      type="number"
                      step="0.01"
                      value={newService.configuration.additional_hour_price}
                      onChange={(e) => setNewService(prev => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          additional_hour_price: parseFloat(e.target.value) || 45
                        }
                      }))}
                      className="block w-full rounded-lg border border-gray-200 pl-10 pr-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setIsAddingService(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateService}
                disabled={!newService.service_name || newService.base_price <= 0 || isLoading}
                className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Service
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}